import type { LongDroughtEvent } from '../../analytics/long-droughts.ts';
import { formatDecimal, formatInteger, formatUtcStamp } from '../../shared/format.ts';

export function LongDroughtTimeline(props: {
  events: readonly LongDroughtEvent[];
  firstSequence: number | null;
  lastSequence: number | null;
}) {
  if (props.events.length === 0) {
    return <p className="text-sm text-muted">No completed droughts meet this threshold.</p>;
  }
  const minSeq = Math.min(
    props.firstSequence ?? props.events[0]!.startSequence,
    ...props.events.map((event) => event.startSequence),
  );
  const maxSeq = Math.max(
    props.lastSequence ?? props.events[props.events.length - 1]!.startSequence,
    ...props.events.map((event) => event.startSequence),
  );
  const maxLength = Math.max(...props.events.map((event) => event.lengthRounds), 1);
  const left = 36;
  const top = 16;
  const height = 180;
  const plotWidth = 640;
  const width = left + plotWidth + 12;
  const span = Math.max(1, maxSeq - minSeq);
  const xFor = (sequence: number) => left + ((sequence - minSeq) / span) * plotWidth;
  const yFor = (length: number) => top + height - (length / maxLength) * height;

  return (
    <div className="overflow-x-auto">
      <svg role="img" aria-label="Long drought timeline" width={width} height={top + height + 28} className="text-ink">
        <title>Long drought timeline</title>
        <desc>Scatter of completed long drought events by start sequence and drought length.</desc>
        <line x1={left} y1={top} x2={left} y2={top + height} stroke="currentColor" strokeOpacity="0.25" />
        <line
          x1={left}
          y1={top + height}
          x2={left + plotWidth}
          y2={top + height}
          stroke="currentColor"
          strokeOpacity="0.25"
        />
        <text x={4} y={top + 8} className="fill-muted" fontSize="10">
          {formatInteger(maxLength)}
        </text>
        <text x={4} y={top + height} className="fill-muted" fontSize="10">
          0
        </text>
        {props.events.map((event) => {
          const x = xFor(event.startSequence);
          const y = yFor(event.lengthRounds);
          return (
            <g key={`${event.startSequence}-${event.lengthRounds}`}>
              <line x1={x} y1={top + height} x2={x} y2={y} stroke="#3f5d56" strokeOpacity="0.35" />
              <circle cx={x} cy={y} r="4" fill="#3f5d56">
                <title>
                  {`Drought length: ${formatInteger(event.lengthRounds)}\nStart sequence: ${formatInteger(event.startSequence)}\nEnd sequence: ${formatInteger(event.endSequence)}\nTerminating hit sequence: ${formatInteger(event.terminatingHitSequence)}\nStart time: ${formatUtcStamp(event.startTimestampUtc)}\nEnd time: ${formatUtcStamp(event.endTimestampUtc)}\nRounds since previous long drought: ${formatInteger(event.roundsSincePreviousStart)}\nHours since previous long drought: ${formatDecimal(event.hoursSincePreviousStart, 1)}`}
                </title>
              </circle>
            </g>
          );
        })}
        <text x={left} y={top + height + 16} className="fill-muted" fontSize="9">
          {formatInteger(minSeq)}
        </text>
        <text x={left + plotWidth} y={top + height + 16} textAnchor="end" className="fill-muted" fontSize="9">
          {formatInteger(maxSeq)}
        </text>
      </svg>
      <p className="mt-1 text-xs text-muted">X: start sequence · Y: drought length</p>
    </div>
  );
}
